import React, { useState } from "react";
import { isValid, parseISO } from "date-fns";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import Wrapper from "~/shared/ui/Wrapper";
import AppealConfimration from "~/widgets/Modals/AddAppeal/ui/AppealConfirmation";
import { useAppeals, useSetAppeal } from "~/entity/Appeals";
import {
  IAppealPeriodShema,
  IAppealSchema,
  TAppealFilter,
} from "~/entity/Appeals/model/schema";
import { useSetLocationData } from "~/hooks/locationData";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import {
  toServerAppeal,
  toServerAppealDecision,
} from "~/pages/Appeal/model/adapter";
import { useAppealServerFilter } from "~/pages/Appeal/model/filterAdapter";
import { APPEALS_OPTIONS_TABLE } from "~/pages/Appeal/model/options";
import { getAppealsColumns } from "~/pages/Appeal/model/tableAdapter";
import { usePermissions } from "~/shared/context/PermissionContext";
import { TableV2 } from "~/shared/ui/TableV2";
import { AppealAdminControl } from "~/widgets/AppealAdminControl";
import { AppealsControl } from "~/widgets/ControlComponent/AppealsControl/AppealsControl";
import { AddAppeal } from "~/widgets/Modals/AddAppeal";
import { IAppealSendForm } from "~/widgets/Modals/AddAppeal/model/schema";
import { AppealComment } from "~/widgets/Modals/AppealComment";
import { AppealView } from "~/widgets/Modals/AppealView";

import AppealsFilter from "./AppealsFilter";

import s from "./Appeal.less";

export interface AppealStateFormIProps {
  value: IAppealSendForm & { submitType: "draft" | "sent" };
  appeal: IAppealSchema;
}

export const Appeal = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  const [stateFilterForAppeal, setStateFilterForAppeal] = useState(null);
  const [dataConfirm, setDataConfirm] = useState<AppealStateFormIProps | null>(
    null,
  );
  const setQuerySearch = useURLParamsSetter();
  const { openModal, currentId, ...settedFilter } = useURLParamsGetter<
    TAppealFilter & { openModal: string; currentId: string }
  >();
  const { queryKeys, getAppeal, getAppealPeriod } = useAppeals();
  const setData = useSetLocationData();

  const serverFilter = useAppealServerFilter(settedFilter);

  const { data, isLoading, refetch } = useQuery({
    queryKey: queryKeys.all(settedFilter),
    queryFn: () => getAppeal(serverFilter),
    enabled: !!Object.keys(settedFilter).length,
  });

  const hasAppealPeriodsGetPermission = hasPermission(
    "employeeAccounting.appeal.periods.get",
  );

  const appealPeriodQuery = useQuery({
    queryFn: () => getAppealPeriod(),
    queryKey: ["appealPeriods"],
    enabled: hasAppealPeriodsGetPermission,
  });

  const periodsData = get(
    appealPeriodQuery.data,
    "data.periods",
    [],
  ) as IAppealPeriodShema[];
  const safeParseISO = (value?: string): Date | undefined => {
    if (!value) return undefined;
    const d = parseISO(value);
    return isValid(d) ? d : undefined;
  };

  const appeals: IAppealSchema[] = get(data, "data.appeals", []);
  const {
    saveDraft,
    saveAndSentAppeal,
    resolveAppeal,
    setAppealDecisionMutation,
  } = useSetAppeal();

  const onDownload = () => {
    console.log(
      "eslint ругался на пустую функцию, но что здесь вообще было??? и было ли",
    );
  };

  const onSetAppealSubmit = (
    data: IAppealSendForm & { submitType: "draft" | "sent" },
    appeal: IAppealSchema,
  ) => {
    const fileIdsOld = appeal.files
      .filter((file) => file.scope === "appeal")
      .map((f) => String(f.fileId));
    if (data.submitType === "draft") {
      saveDraft.mutate(toServerAppeal(data, currentId, fileIdsOld, "appeal"), {
        onSuccess: () => {
          setData("openModal", "");
        },
      });
    } else {
      saveAndSentAppeal.mutate(
        toServerAppeal(data, currentId, fileIdsOld, "appeal"),
        {
          onSuccess: () => {
            setData("openModal", "");
          },
        },
      );
    }
  };

  const onSetAppealResolveSubmit = (
    data: IAppealSendForm,
    appeal: IAppealSchema,
  ) => {
    const fileIdsOld = appeal.files
      .filter((file) => file.scope === "resolution")
      .map((f) => String(f.fileId));
    resolveAppeal.mutate(
      toServerAppeal(data, currentId, fileIdsOld, "resolution"),
      {
        onSuccess: () => {
          setData("openModal", "");
        },
      },
    );
  };
  const onSetAppealDecision = (
    data: IAppealSendForm,
    { id }: IAppealSchema,
  ) => {
    setAppealDecisionMutation.mutate(toServerAppealDecision(data, id), {
      onSuccess: () => {
        setData("openModal", "");
        refetch();
      },
    });
  };

  const getDataConfirm = (data: AppealStateFormIProps) => {
    setDataConfirm(data);
  };
  const onCloseAppealConfirmation = () => {
    setDataConfirm(null);
  };

  const onOpenFilesModal = (id: string | number) => () => {
    setData("openModal", "appealFiles", "currentId", id);
  };

  const onOpenAddAppealModal = (id: string | number) => () => {
    setData("openModal", "addAppeal", "currentId", id);
  };
  const onOpenResolveAppealModal = (id: string | number) => () => {
    setData("openModal", "closeAppeal", "currentId", id);
  };

  const onOpenAppealCommentModal = (id: string | number) => () => {
    setData("openModal", "addComment", "currentId", id);
  };
  const onOpenEditorAppealModal = (id: string | number) => () => {
    setData("openModal", "editor", "currentId", id);
  };

  const hasAppealReportPermission = hasPermission(
    "employeeAccounting.appeal.report",
  );

  return (
    <Wrapper.Content
      title={"Статус Малуса по обучению"}
      filterComponent={<AppealsFilter />}
      controlComponent={
        hasAppealReportPermission && (
          <AppealsControl onDownload={onDownload} filter={serverFilter} />
        )
      }
    >
      {hasAppealPeriodsGetPermission && !appealPeriodQuery.isLoading && (
        <AppealAdminControl
          isViewOnly={
            !hasPermission("employeeAccounting.appeal.periods.upsert")
          }
          defaultValues={{
            period: {
              from: safeParseISO(periodsData[0]?.startDate),
              to: safeParseISO(periodsData[0]?.endDate),
            },
            date: safeParseISO(periodsData[0]?.decisionDate),
          }}
          onPeriodChange={() => {
            refetch();
            appealPeriodQuery.refetch();
          }}
        />
      )}

      <TableV2
        columns={getAppealsColumns(t, APPEALS_OPTIONS_TABLE, periodsData, {
          onOpenFilesModal,
          onOpenAddAppealModal,
          onOpenResolveAppealModal,
          onOpenAppealCommentModal,
          onOpenEditorAppealModal,
        })}
        dataSource={appeals}
        loading={isLoading}
        wrapperClassName={s.table}
        pagination={false}
      />
      <AddAppeal
        scope={
          openModal === "addAppeal"
            ? "appeal"
            : openModal === "editor"
              ? "editor"
              : "resolution"
        }
        isOpenModal={
          openModal === "addAppeal" ||
          openModal === "closeAppeal" ||
          openModal === "editor"
        }
        isCloseAppealByAdmin={openModal === "closeAppeal"}
        onSubmit={
          openModal === "addAppeal"
            ? onSetAppealSubmit
            : openModal === "editor"
              ? onSetAppealDecision
              : onSetAppealResolveSubmit
        }
        getDataConfirmation={getDataConfirm}
      />
      <AppealView />
      <AppealComment />
      <AppealConfimration
        close={onCloseAppealConfirmation}
        openModal={!!dataConfirm}
        dataConfirmation={dataConfirm}
        onSubmit={onSetAppealResolveSubmit}
      />
    </Wrapper.Content>
  );
};
